import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { Empty } from "antd";

// DCA-C3 flow-regime diagnostics (FE). "Diagnose before you fit": this is a
// read-only diagnostic view -- it does NOT produce EUR. It renders two charts
// from the BE diagnostics endpoint:
//
//   1) Log-log: log(q) and the Bourdet log-derivative (deriv = t*dq/dt) vs
//      log(t), with diagnostic slope reference lines overlaid so the engineer
//      can read the flow regime:
//        -1/2  -> transient linear flow      (Duong / SEPD)
//        -1/4  -> bilinear flow              (Duong)
//        -1    -> boundary-dominated flow    (Arps low-b / exponential)
//   2) Rate-Cum: q vs Np (cumulative). Boundary-dominated (exponential)
//      decline plots as a straight line here.
//
// Identified regime segments from the BE (`regimes`) are annotated on the
// log-log chart as colored bands + labels.
//
// Graceful degradation: when `data` is null (endpoint 404 / not deployed) or
// the arrays are empty, an empty-state hint is shown and nothing crashes.

// Regime -> color map (kept local so this component does not depend on the
// well-status palette; these are diagnostic-band tints, not status codes).
const REGIME_COLORS = {
  linear_flow: "rgba(46, 139, 87, 0.18)", // green   = -1/2 linear
  linear: "rgba(46, 139, 87, 0.18)",
  bilinear: "rgba(65, 105, 225, 0.18)", // blue    = -1/4 bilinear
  bilinear_flow: "rgba(65, 105, 225, 0.18)",
  bdf: "rgba(255, 140, 0, 0.18)", // orange  = boundary-dominated
  boundary: "rgba(255, 140, 0, 0.18)",
  boundary_dominated: "rgba(255, 140, 0, 0.18)",
  exponential: "rgba(255, 140, 0, 0.18)",
  transition: "rgba(160, 160, 160, 0.15)", // grey    = transition
};

const REGIME_LABELS = {
  linear_flow: "Linear (-1/2)",
  linear: "Linear (-1/2)",
  bilinear: "Bilinear (-1/4)",
  bilinear_flow: "Bilinear (-1/4)",
  bdf: "BDF / boundary",
  boundary: "BDF / boundary",
  boundary_dominated: "BDF / boundary",
  exponential: "BDF / exponential",
  transition: "Transition",
};

const AXIS_TEXT = "#aaa";
const SLOPE_REF = "rgba(255,255,255,0.35)";

function regimeColor(regime) {
  if (!regime) return "rgba(160,160,160,0.15)";
  return REGIME_COLORS[String(regime).toLowerCase()] || "rgba(160,160,160,0.15)";
}

function regimeLabel(r) {
  const key = String(r.flow_regime || "").toLowerCase();
  const base = REGIME_LABELS[key] || r.flow_regime || "regime";
  const slope = r.slope != null && !isNaN(r.slope) ? ` m=${Number(r.slope).toFixed(2)}` : "";
  return base + slope;
}

// Build a straight reference line of given log-log slope passing near the
// data cloud, returned as [[t,q],...] in linear coords (ECharts 'log' axes
// handle the log mapping). We anchor the line to pass through the centroid of
// the q-cloud so it sits visually amid the data.
function slopeRefLine(loglog, slope) {
  const pts = loglog.filter(
    (p) => p && p.t > 0 && p.q > 0 && isFinite(p.t) && isFinite(p.q)
  );
  if (pts.length < 2) return null;
  const tMin = Math.min(...pts.map((p) => p.t));
  const tMax = Math.max(...pts.map((p) => p.t));
  // anchor: geometric-mean point of the cloud
  const lnT = pts.map((p) => Math.log(p.t));
  const lnQ = pts.map((p) => Math.log(p.q));
  const t0 = Math.exp(lnT.reduce((a, b) => a + b, 0) / lnT.length);
  const q0 = Math.exp(lnQ.reduce((a, b) => a + b, 0) / lnQ.length);
  // q = q0 * (t/t0)^slope
  const qAt = (t) => q0 * Math.pow(t / t0, slope);
  return [
    [tMin, qAt(tMin)],
    [tMax, qAt(tMax)],
  ];
}

const DcaDiagnosticsChart = (props) => {
  const { data, title } = props;

  const loglog = useMemo(
    () => (Array.isArray(data?.loglog) ? data.loglog : []),
    [data]
  );
  const rateCum = useMemo(
    () => (Array.isArray(data?.rate_cum) ? data.rate_cum : []),
    [data]
  );
  const regimes = useMemo(
    () => (Array.isArray(data?.regimes) ? data.regimes : []),
    [data]
  );

  const hasLogLog = loglog.length > 0;
  const hasRateCum = rateCum.length > 0;
  const hasAny = hasLogLog || hasRateCum;

  // ---- Log-log option ----
  const logLogOption = useMemo(() => {
    if (!hasLogLog) return null;

    const qSeriesData = loglog
      .filter((p) => p && p.t > 0 && p.q > 0)
      .map((p) => [p.t, p.q]);
    const derivSeriesData = loglog
      .filter((p) => p && p.t > 0 && p.deriv != null && Math.abs(p.deriv) > 0)
      .map((p) => [p.t, Math.abs(p.deriv)]);

    // Diagnostic slope reference lines (custom markLine via extra line series).
    const refDefs = [
      { slope: -0.5, name: "-1/2 linear" },
      { slope: -0.25, name: "-1/4 bilinear" },
      { slope: -1.0, name: "-1 boundary" },
    ];
    const refSeries = refDefs
      .map((d) => {
        const line = slopeRefLine(loglog, d.slope);
        if (!line) return null;
        return {
          name: d.name,
          type: "line",
          data: line,
          showSymbol: false,
          silent: true,
          lineStyle: { type: "dashed", color: SLOPE_REF, width: 1 },
          z: 1,
          endLabel: {
            show: true,
            formatter: d.name,
            color: SLOPE_REF,
            fontSize: 10,
          },
          tooltip: { show: false },
        };
      })
      .filter(Boolean);

    // Regime bands as markArea on the q series (x-range from..to in t).
    const markAreaData = regimes
      .filter((r) => r && r.from != null && r.to != null)
      .map((r) => [
        {
          xAxis: r.from,
          itemStyle: { color: regimeColor(r.flow_regime) },
          label: {
            show: true,
            formatter: regimeLabel(r),
            color: "#ddd",
            fontSize: 10,
            position: "insideTop",
          },
        },
        { xAxis: r.to },
      ]);

    return {
      title: {
        text: title ? `${title} — Flow-Regime Diagnostic (log-log)` : "Flow-Regime Diagnostic (log-log)",
        left: "center",
        top: 4,
        textStyle: { color: "rgba(255,255,255,0.7)", fontSize: 13 },
      },
      legend: {
        top: 26,
        textStyle: { color: "#ccc" },
        data: ["q (rate)", "Bourdet derivative", "-1/2 linear", "-1/4 bilinear", "-1 boundary"],
      },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
      },
      toolbox: {
        right: 8,
        top: 0,
        itemSize: 14,
        feature: { dataZoom: {}, restore: {}, saveAsImage: { type: "png" } },
      },
      grid: { left: 54, right: 54, top: 58, bottom: 46, containLabel: true },
      xAxis: {
        type: "log",
        name: "t (yr)",
        nameLocation: "middle",
        nameGap: 26,
        nameTextStyle: { color: AXIS_TEXT },
        axisLabel: { color: AXIS_TEXT },
        splitLine: { lineStyle: { opacity: 0.15, color: "#fff" } },
      },
      yAxis: {
        type: "log",
        name: "q / |deriv|",
        nameTextStyle: { color: AXIS_TEXT },
        axisLabel: { color: AXIS_TEXT },
        splitLine: { lineStyle: { opacity: 0.15, color: "#fff" } },
      },
      series: [
        {
          name: "q (rate)",
          type: "line",
          data: qSeriesData,
          symbol: "circle",
          symbolSize: 4,
          itemStyle: { color: "#5470c6" },
          lineStyle: { color: "#5470c6" },
          z: 3,
          markArea: markAreaData.length ? { silent: true, data: markAreaData } : undefined,
        },
        {
          name: "Bourdet derivative",
          type: "line",
          data: derivSeriesData,
          symbol: "emptyCircle",
          symbolSize: 4,
          itemStyle: { color: "#fac858" },
          lineStyle: { color: "#fac858" },
          z: 2,
        },
        ...refSeries,
      ],
    };
  }, [hasLogLog, loglog, regimes, title]);

  // ---- Rate-Cum option ----
  const rateCumOption = useMemo(() => {
    if (!hasRateCum) return null;
    const pts = rateCum
      .filter((p) => p && p.np != null && p.q != null)
      .map((p) => [p.np, p.q]);

    return {
      title: {
        text: "Rate vs Cumulative (boundary-dominated = straight line)",
        left: "center",
        top: 4,
        textStyle: { color: "rgba(255,255,255,0.7)", fontSize: 13 },
      },
      tooltip: { trigger: "axis", axisPointer: { type: "cross" } },
      toolbox: {
        right: 8,
        top: 0,
        itemSize: 14,
        feature: { dataZoom: {}, restore: {}, saveAsImage: { type: "png" } },
      },
      grid: { left: 54, right: 24, top: 40, bottom: 46, containLabel: true },
      xAxis: {
        type: "value",
        name: "Np (cum)",
        nameLocation: "middle",
        nameGap: 26,
        nameTextStyle: { color: AXIS_TEXT },
        axisLabel: { color: AXIS_TEXT },
        splitLine: { lineStyle: { opacity: 0.15, color: "#fff" } },
      },
      yAxis: {
        type: "value",
        name: "q (rate)",
        nameTextStyle: { color: AXIS_TEXT },
        axisLabel: { color: AXIS_TEXT },
        splitLine: { lineStyle: { opacity: 0.15, color: "#fff" } },
      },
      series: [
        {
          name: "q vs Np",
          type: "line",
          data: pts,
          symbol: "circle",
          symbolSize: 4,
          itemStyle: { color: "#73c0de" },
          lineStyle: { color: "#73c0de" },
        },
      ],
    };
  }, [hasRateCum, rateCum]);

  if (!hasAny) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        style={{ color: "rgba(255,255,255,0.6)", paddingTop: 60 }}
        description={
          data == null
            ? "Flow-regime diagnostics not available yet (endpoint pending)."
            : "No diagnostic data for the selected well/phase."
        }
      />
    );
  }

  return (
    <div className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
      <div style={{ flex: "1 1 60%", minHeight: 220 }}>
        {logLogOption ? (
          <ReactECharts
            option={logLogOption}
            notMerge={true}
            style={{ height: "100%", width: "100%" }}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ color: "rgba(255,255,255,0.6)", paddingTop: 30 }}
            description="No log-log diagnostic series."
          />
        )}
      </div>
      <div style={{ flex: "1 1 40%", minHeight: 180 }}>
        {rateCumOption ? (
          <ReactECharts
            option={rateCumOption}
            notMerge={true}
            style={{ height: "100%", width: "100%" }}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ color: "rgba(255,255,255,0.6)", paddingTop: 20 }}
            description="No rate-cum series."
          />
        )}
      </div>
    </div>
  );
};

export default DcaDiagnosticsChart;
